/**
 * Start Message types for the web application
 */

// Base entity interface
interface BaseEntity {
  readonly id: string;
  readonly tenantId: string;
  readonly entityType: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly version: number;
}

// Core Start Message types
export interface StartMessage extends BaseEntity {
  readonly entityType: "START_MESSAGE";
  readonly name: string;
  readonly content: string; // Max 160 characters including tracking space
  readonly isActive: boolean;
  readonly usageCount: number;
  readonly tags?: readonly string[];
  readonly conversationIds: readonly string[];
}

export interface StartMessageWithTracking {
  readonly message: StartMessage;
  readonly trackingData: string; // Format: {shortUserId}-{shortConversationId}
  readonly fullContent: string; // Message + tracking data
  readonly trackingLink?: string; // For SMS integration
  readonly previewSMS: string;
  readonly previewChat: string;
}

export interface StartMessageConnection {
  readonly items: readonly StartMessage[];
  readonly nextToken?: string;
  readonly total: number;
}

// Input types for GraphQL operations
export interface CreateStartMessageInput {
  readonly name: string;
  readonly content: string; // Max 160 chars including tracking space
  readonly tags?: readonly string[];
  readonly isActive?: boolean;
}

export interface UpdateStartMessageInput {
  readonly id: string;
  readonly name?: string;
  readonly content?: string;
  readonly tags?: readonly string[];
  readonly isActive?: boolean;
}

export interface StartMessageFilterInput {
  readonly conversationId?: string;
  readonly isActive?: boolean;
  readonly tags?: readonly string[];
  readonly search?: string;
}

// Error types
export enum StartMessageErrorCode {
  MESSAGE_NOT_FOUND = "MESSAGE_NOT_FOUND",
  MESSAGE_TOO_LONG = "MESSAGE_TOO_LONG",
  CONVERSATION_NOT_FOUND = "CONVERSATION_NOT_FOUND",
  ALREADY_ATTACHED = "ALREADY_ATTACHED",
  NOT_ATTACHED = "NOT_ATTACHED",
  VALIDATION_FAILED = "VALIDATION_FAILED",
  TENANT_ISOLATION_VIOLATION = "TENANT_ISOLATION_VIOLATION",
}

export class StartMessageError extends Error {
  constructor(
    public readonly code: StartMessageErrorCode,
    message: string,
    public readonly details?: Record<string, unknown>
  ) {
    super(message);
    this.name = "StartMessageError";
  }
}

// Web-specific types
export interface StartMessageFormData {
  name: string;
  content: string;
  tags: string[];
  isActive: boolean;
}

export interface StartMessageValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  characterCount: number;
  remainingCharacters: number;
}

export interface StartMessagePreview {
  smsFormat: string;
  chatFormat: string;
  trackingData: string;
  fullContent: string;
}

export interface StartMessageStats {
  total: number;
  active: number;
  totalUsage: number;
  averageLength: number;
}

// Form validation constants
export const START_MESSAGE_CONSTANTS = {
  MAX_CONTENT_LENGTH: 160,
  TRACKING_DATA_LENGTH: 20, // Estimated space for tracking
  EFFECTIVE_MAX_LENGTH: 140, // 160 - 20 for tracking
  WARNING_THRESHOLD: 0.9, // Show warning at 90% capacity
  MAX_NAME_LENGTH: 100,
  MAX_TAGS: 10,
} as const;

// Character count status
export type CharacterCountStatus = "safe" | "warning" | "error";

export interface CharacterCountInfo {
  count: number;
  remaining: number;
  status: CharacterCountStatus;
  percentage: number;
}
